import React, { useState, useEffect, useRef } from 'react';
import apiClient from 'services/api';
import { lerToken, iniciarSessao } from 'services/sessao';
import Cookies from 'js-cookie';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { fetchAndSetCidadeEstadoCookie } from 'services/userLocationService';
import Icon from 'components/Icon';
import { ehAndroidNoNavegador, ehNativo } from 'services/plataforma';
import { appAndroidPublicado } from 'config/appAndroid';
import SeloGooglePlay from 'components/SeloGooglePlay';
import { useOnline } from 'hooks/useOnline';
import { APP_VERSION } from 'config/version';
import useMetadadosPagina from 'hooks/useMetadadosPagina';
import { voltarAoModoPadrao } from 'hooks/useModoConta';
import { esquecerSnapshots } from 'services/dashboardService';
import { textoDoErro } from 'services/erros';

// A mesma frase nos dois pontos em que a falta de rede aparece — antes de enviar (o aparelho já
// sabe que está sem rede, AUT-302) e na falha da requisição (a rede caiu no meio, ou "conectado"
// sem saída, AUT-206). Códigos distintos porque o diagnóstico é distinto.
//
// Nenhuma mensagem do login aceita o texto do backend: a tela não pode dizer se o CPF existe.
const SEM_SERVIDOR = { usarMensagemDoServidor: false };

/**
 * <h2>SEM reCAPTCHA NO APP</h2>
 * O reCAPTCHA v3 pontua "isto parece um navegador com histórico de gente de verdade". A WebView de
 * um app é, por construção, o oposto disso: contexto novo a cada instalação, sem cookies do Google
 * e sem sinais. O resultado medido foi **score 0.1** — o piso da escala, o valor que o Enterprise
 * dá quando está confiante de que é automação — e o backend recusa abaixo de 0.5 com 403
 * ("Bot detectado"), deixando o login impossível.
 *
 * Isso apareceu num emulador, mas não é problema de emulador: usuário real com instalação nova cai
 * na mesma falta de sinais. Um aparelho que passou passou por sorte.
 *
 * Então no app o desafio é pulado inteiro — nem o script é baixado. **O que protege o login nativo
 * é o rate limit do backend** (ratelimit/RouteClassifier), não este arquivo.
 *
 * Duas coisas que vale saber antes de mexer aqui:
 *
 *  - o `/login` NUNCA exigiu o captcha do lado do servidor. A verificação vive num endpoint
 *    próprio (`/login/verify-recaptcha`) e o `/login` aceita qualquer POST — ou seja, pular o
 *    desafio aqui não abre nada que já não estivesse aberto a um script;
 *  - por isso também não há header de "sou o app" nesta chamada. Header não é prova de origem, e
 *    um que não gateia nada só sugere uma proteção inexistente.
 *
 * O caminho para ter proteção de bot no app é o SDK Android do reCAPTCHA Enterprise, com chave
 * atrelada ao pacote — registrado em prompts/publicacao-play-store.md.
 */

// Tempo máximo que esperamos pelo grecaptcha.enterprise.execute() antes de
// desistir e mostrar uma mensagem amigável. Sem isso, uma rede lenta/instável
// deixa o usuário travado no loading até o próprio script do Google estourar
// um erro interno (não capturável) minutos depois.
const RECAPTCHA_TIMEOUT_MS = 8000;

/**
 * A mensagem do 429, a partir do `Retry-After` que o backend manda.
 *
 * Até aqui isto era o texto fixo "aguarde 2 minutos", que correspondia ao balde plano do rate limit.
 * A espera passou a ser CRESCENTE (30s, 1min, 2min, 4min... — ver ratelimit/BackoffDeLogin no
 * backend), então um número fixo passou a estar errado das duas formas possíveis: mandando esperar
 * demais quem podia tentar em 30 segundos, e mandando tentar cedo quem ainda tem 15 minutos de
 * espera — o que só produz outra recusa.
 *
 * O header é lido com cuidado porque ele pode não chegar: só vale se `Retry-After` estiver em
 * `exposedHeaders` no CORS do backend (está), e o app fala com outra origem. Sem ele, a mensagem não
 * chuta número nenhum — dizer "aguarde um momento" é vago, dizer um valor errado é pior.
 */
const mensagemDeEspera = (retryAfter) => {
	const segundos = Number(retryAfter);
	if (!Number.isFinite(segundos) || segundos <= 0) {
		return 'Muitas tentativas de login. Aguarde um momento e tente novamente.';
	}

	if (segundos < 60) {
		return `Muitas tentativas de login. Aguarde ${Math.ceil(segundos)} segundos e tente novamente.`;
	}

	const minutos = Math.ceil(segundos / 60);
	return `Muitas tentativas de login. Aguarde ${minutos} ${minutos === 1 ? 'minuto' : 'minutos'} e tente novamente.`;
};

/**
 * Máscara progressiva de CPF: 1234567 -> 123.456.7, 12345678901 -> 123.456.789-01. Só os 11
 * primeiros dígitos. Caseira, e não o InputMask das outras telas, porque este campo também recebe
 * e-mail -- uma máscara fixa não serve para os dois. O placeholder mostra a máscara, então o campo
 * precisa cumpri-la: um CPF cru (12345678900) ao lado de "000.000.000-00" pareceria quebrado.
 */
const mascararCpf = (digitos) => {
	const d = digitos.slice(0, 11);
	return d
		.replace(/^(\d{3})(\d)/, '$1.$2')
		.replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
		.replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
};

/**
 * O que o "Criar conta" leva para o cadastro: o CPF ou o e-mail que a pessoa já digitou aqui, para
 * ela não digitar de novo na etapa 2. Vai no `state` da navegação, e não na URL, porque um CPF na
 * query string fica no histórico, nos logs do servidor e no analytics. Mesma regra do `autenticar`
 * (tem `@` é e-mail). CPF pela metade não vai: no cadastro ele só acusaria erro.
 */
const dadosParaOCadastro = (valor) => {
	if (valor.includes('@')) return { emailInicial: valor.trim() };
	const digitos = valor.replace(/\D/g, '');
	return digitos.length === 11 ? { cpfInicial: digitos } : undefined;
};

const Login = () => {
	// noindex: tela utilitária, sem valor na busca — e é para onde as rotas privadas redirecionam
	// o Googlebot, que não tem sessão.
	useMetadadosPagina({ titulo: 'Entrar | Nova Faxina', indexar: false });

	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState('');
	const online = useOnline();
	const [credenciaisInvalidas, setCredenciaisInvalidas] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [capsLockOn, setCapsLockOn] = useState(false);
	// O campo de senha começa escondido: a tela abre só com o e-mail/CPF e a senha aparece quando
	// a pessoa preenche esse campo e sai dele. É uma via de mão única -- depois de revelado não
	// volta a sumir nem que o e-mail seja apagado, porque um campo que some debaixo do cursor é
	// pior do que um a mais na tela. Também conta como revelado se a senha já tem valor: o
	// gerenciador de senhas do navegador preenche os dois campos de uma vez, e um botão "Entrar"
	// habilitado por uma senha que a pessoa não vê seria inexplicável.
	const [senhaRevelada, setSenhaRevelada] = useState(false);
	const mostrarSenha = senhaRevelada || password !== '';
	// Decide o ícone do campo: cartão de identidade enquanto o campo está vazio (acompanha o
	// placeholder, que é a máscara de CPF) ou só tem número; a cartinha entra quando aparece
	// letra ou @.
	const digitandoEmail = /[^\d.-]/.test(username);
	// Quem revela a senha por Tab/Enter/saída do campo quer o cursor nela. O foco vai no efeito
	// que tira o `inert`, e não aqui, porque um elemento inert não é focável -- ver NavBar.
	const focarSenhaAoRevelar = useRef(false);
	const senhaConteudoRef = useRef(null);
	const passwordRef = useRef(null);
	const navigate = useNavigate();
	const location = useLocation();
	// Se o usuário chegou aqui por ter tentado acessar uma rota protegida (ex: "Buscar
	// Profissional" na Home sem estar logado), volta pra lá após autenticar. Sem essa
	// origem, mantém o comportamento padrão de ir para o dashboard.
	const destinoPosLogin = location.state?.redirecionarPara || '/dashboard';

	const salvarCidadeEstadoUsuario = async (token) => {
		const decodedToken = jwtDecode(token);
		const userId = decodedToken?.userId;

		if (!userId) {
			Cookies.remove('cidadeEstadoUsuario');
			return;
		}

		await fetchAndSetCidadeEstadoCookie(userId);
	};

	/**
	 * Pede o token ao reCAPTCHA. O callback de `ready` vira Promise para que o `await` de quem
	 * chama espere de fato o token, e não só o registro do callback.
	 */
	const obterTokenRecaptcha = () => new Promise((resolve, reject) => {
		if (!window.grecaptcha?.enterprise) {
			reject(new Error('reCAPTCHA não carregado'));
			return;
		}

		window.grecaptcha.enterprise.ready(async () => {
			try {
				// Promise.race com timeout próprio: se a rede até o Google travar, falhamos rápido
				// em vez de deixar o script do reCAPTCHA estourar um erro não tratado minutos
				// depois, em cima de qualquer tela em que o usuário já estiver.
				resolve(await Promise.race([
					window.grecaptcha.enterprise.execute(process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY, { action: 'LOGIN' }),
					new Promise((_, rejeitarPorTempo) =>
						setTimeout(() => rejeitarPorTempo(new Error('reCAPTCHA demorou demais para responder')), RECAPTCHA_TIMEOUT_MS)
					),
				]));
			} catch (erro) {
				reject(erro);
			}
		});
	});

	/**
	 * O desafio inteiro. `true` libera o login; `false` significa que a mensagem já está na tela.
	 *
	 * As três falhas possíveis têm mensagens distintas, e isso é o que faltava: antes as três
	 * terminavam em "Verifique sua conexão", mandando a pessoa olhar o wi-fi por um problema que
	 * podia ser score baixo — e obrigando quem depura a abrir o DevTools da WebView para saber
	 * qual das três tinha acontecido.
	 */
	const passarPeloRecaptcha = async () => {
		let token;
		try {
			token = await obterTokenRecaptcha();
		} catch (erro) {
			// Falha do lado do cliente: script não carregado ou execute() estourando o timeout.
			console.error('[recaptcha] falha no cliente:', erro.message);
			setError(textoDoErro('AUT-301', erro, SEM_SERVIDOR));
			setCredenciaisInvalidas(false);
			return false;
		}

		try {
			const { data } = await apiClient.post('/login/verify-recaptcha', { token, action: 'LOGIN' });
			if (data === 'Sucesso') return true;

			setError(textoDoErro('AUT-201'));
		} catch (erro) {
			// 403 = score abaixo de 0.5 ("Bot detectado"); 500 = o Google recusou o token. Ver
			// AuthController.verify. Nenhum dos dois é problema de conexão.
			const status = erro?.response?.status;
			console.error('[recaptcha] recusado pelo backend, status:', status ?? erro?.message);

			setError(textoDoErro(status === 403 || status === 500 ? 'AUT-202' : 'AUT-203', erro, SEM_SERVIDOR));
		}

		setCredenciaisInvalidas(false);
		return false;
	};

	const autenticar = async () => {
		try {
			// O CPF vai só com dígitos, como sempre foi: a máscara é da tela. O backend até aceita
			// pontuado (AuthController tira `.` e `-`), mas não há por que depender disso.
			const { data } = await apiClient.post('/login', {
				username: username.includes('@') ? username : username.replace(/\D/g, ''),
				password,
			});

			// O par inteiro: `data` traz token e refreshToken. Ver services/sessao.
			iniciarSessao(data);
			// Todo login abre no modo trabalhando (ver hooks/useModoConta).
			voltarAoModoPadrao();
			// E sem os números do painel da sessão anterior (o cache da troca de modo).
			esquecerSnapshots();

			// O cookie de cidade é acessório, e falhar nele não pode transformar um login que deu
			// certo em mensagem de erro: o token já está salvo neste ponto. Antes esta chamada
			// corria dentro do try do reCAPTCHA e uma falha dela mostrava "não foi possível
			// concluir a verificação de segurança" para quem já estava autenticado.
			await salvarCidadeEstadoUsuario(data.token).catch(() => {});

			// `replace`: o login não pode ficar no histórico como destino do botão voltar. Voltar
			// para cá com a sessão já aberta só faz o efeito abaixo reenviar a pessoa ao mesmo
			// destino -- e no app, onde "/" já não é tela (ver RaizDoApp em App.jsx), esta passa a
			// ser a raiz: sem `replace`, o voltar a partir do dashboard fica preso no par
			// login → dashboard em vez de oferecer a saída do aplicativo.
			navigate(destinoPosLogin, { replace: true });
		} catch (erro) {
			if (erro?.response?.status === 429) {
				setError(textoDoErro('AUT-205', erro, {
					...SEM_SERVIDOR,
					mensagem: mensagemDeEspera(erro.response.headers?.['retry-after']),
				}));
				setCredenciaisInvalidas(false);
			} else if (!erro?.response) {
				// A requisição nem chegou ao servidor: sem rede, DNS, timeout. Antes caía no ramo de
				// baixo — "Dados inválidos" e a senha apagada — para quem só estava em modo avião. A
				// senha fica: o problema não é ela, e a pessoa vai tentar de novo quando a rede voltar.
				setError(textoDoErro('AUT-206', erro, SEM_SERVIDOR));
				setCredenciaisInvalidas(false);
			} else {
				setError(textoDoErro('AUT-204', erro, SEM_SERVIDOR));
				setCredenciaisInvalidas(true);
				// Credencial recusada: limpa a senha para o usuário redigitar sem ter que apagar um
				// valor que ele não consegue ver (campo mascarado). O foco vai num setTimeout
				// porque o input só sai do disabled quando isLoading virar false, no finally.
				setPassword('');
				setTimeout(() => passwordRef.current?.focus(), 0);
			}
		}
	};

	const handleLogin = async (e) => {
		e.preventDefault();

		if (!username || !password) {
			setError(textoDoErro('AUT-001'));
			setCredenciaisInvalidas(false);
			return;
		}

		// O aparelho já sabe que não tem rede: nem dispara a requisição. `online` só é confiável
		// quando é false (ver hooks/useOnline), então o caso "conectado mas sem saída" continua
		// sendo tratado na falha da própria chamada, em `autenticar`.
		if (!online) {
			setError(textoDoErro('AUT-302'));
			setCredenciaisInvalidas(false);
			return;
		}

		setError('');
		setCredenciaisInvalidas(false);
		setIsLoading(true);

		try {
			// No app o reCAPTCHA é pulado -- ver a nota SEM reCAPTCHA NO APP no topo do arquivo.
			if (ehNativo() || await passarPeloRecaptcha()) {
				await autenticar();
			}
		} finally {
			setIsLoading(false);
		}
	};

	// Google reCAPTCHA (https://console.cloud.google.com/security/recaptcha)
	// JS para página index, ao inves de ficar no index, foi injetado aqui
	useEffect(() => {
		// No app o script nem é baixado: é ~300 KB de terceiro na primeira tela, para um desafio
		// que não vai ser usado. Ver a nota SEM reCAPTCHA NO APP no topo do arquivo.
		if (ehNativo()) {
			const verificadoLogado = lerToken();
			if (verificadoLogado) navigate(destinoPosLogin, { replace: true });
			return undefined;
		}

		// A visibilidade do badge é via classe no <body> (ver a regra do .grecaptcha-badge em
		// styles/index.css), não via busca direta por .grecaptcha-badge: o script abaixo é
		// async e só cria esse elemento quando termina de carregar, o que pode acontecer
		// depois que o login já redirecionou pra outra tela — um querySelector síncrono aqui
		// perderia essa corrida e o badge ficaria preso visível pelo resto da sessão.
		document.body.classList.add('pagina-login');

		const scriptId = 'recaptcha-enterprise-script';
		if (!document.getElementById(scriptId)) {
			const script = document.createElement('script');
			script.id = scriptId;
			script.src = `https://www.google.com/recaptcha/enterprise.js?render=${process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}`;
			script.async = true;
			script.defer = true;
			document.head.appendChild(script);
		}

		const verificaLogado = lerToken();
		if (verificaLogado) {
			navigate(destinoPosLogin, { replace: true });
		}

		return () => {
			// Importante: NÃO remover a tag <script> nem apagar window.grecaptcha
			// aqui. O enterprise.js pode ter uma requisição de rede em voo (ex:
			// usuário clicou em "Criar conta" antes dela terminar); desmontar o
			// objeto no meio disso faz o callback de timeout do Google estourar
			// como erro não tratado em cima da tela seguinte ("reCAPTCHA Timeout
			// (b)"). Só tiramos a classe que revela o badge — ele volta a aparecer se
			// o usuário voltar pro Login.
			document.body.classList.remove('pagina-login');
		};
	}, [navigate, destinoPosLogin]);

	// Escondido, o campo de senha continua no DOM (é o que permite animar a altura -- ver
	// .campo-revelavel em styles/index.css), então precisa ser inert para não receber Tab nem
	// leitor de tela. Via ref porque o React 18 ainda não aceita `inert` como prop. O foco
	// pedido na revelação vai aqui, depois de tirar o inert, pelo motivo dito lá em cima.
	useEffect(() => {
		const conteudo = senhaConteudoRef.current;
		if (!conteudo) return;
		conteudo.inert = !mostrarSenha;
		if (mostrarSenha) conteudo.removeAttribute('aria-hidden');
		else conteudo.setAttribute('aria-hidden', 'true');

		if (mostrarSenha && focarSenhaAoRevelar.current) {
			focarSenhaAoRevelar.current = false;
			passwordRef.current?.focus();
		}
	}, [mostrarSenha]);

	const revelarSenha = () => {
		if (mostrarSenha || !username) return;
		focarSenhaAoRevelar.current = true;
		setSenhaRevelada(true);
	};

	// Tab e Enter no e-mail/CPF revelam a senha e já levam o cursor até ela. Sem tratar o Tab,
	// o navegador escolhe o próximo focável ANTES de o blur revelar o campo -- e o próximo, com
	// a senha ainda inert, é o link "Criar conta". O Enter, com o botão desabilitado, não fazia
	// nada; agora avança, que é o que a tecla "próximo" do teclado do celular também dispara.
	const handleUsernameKeyDown = (e) => {
		if (mostrarSenha || !username) return;
		if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
			e.preventDefault();
			revelarSenha();
		}
	};

	// Só dígitos (com ou sem os separadores da máscara) é CPF e ganha a máscara; qualquer letra
	// ou @ faz o campo virar e-mail livre, com o saneamento de sempre.
	const handleUsernameChange = (e) => {
		const value = e.target.value;
		const semSeparadores = value.replace(/[.-]/g, '');
		if (/^[0-9]*$/.test(semSeparadores)) {
			setUsername(mascararCpf(semSeparadores));
		} else {
			setUsername(value.replace(/[^a-zA-Z0-9@._-]/g, ''));
		}
	};

	const handlePasswordKeyEvent = (e) => {
		setCapsLockOn(e.getModifierState?.('CapsLock') ?? false);
	};

	return (
		<>
			<div className="min-h-screen flex bg-gradient-to-br from-background to-accent/20">
				{/* Lado esquerdo: Imagem e branding (visível apenas em desktop) */}
				<div
					className="hidden lg:flex lg:w-1/2 min-h-screen bg-cover bg-center flex-col justify-start items-center p-8 relative"
					style={{
						backgroundImage: "url('./images/fundo_login.webp')",
						paddingTop: '30vh'
					}}
				>
					<div className="absolute inset-0 bg-black/20 lg:bg-black/30"></div>
					<div className="relative z-10 text-center text-white max-w-md" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)' }}>
						{/* <p>, e não <h2>: é frase de marca do painel decorativo, não estrutura do
						    documento. Como <h2> ela vinha antes do <h1> "Entrar" e era maior que ele,
						    então quem navega por títulos ouvia primeiro o nível errado. */}
						<p className="text-4xl font-bold mb-4">Bem-vindo de volta</p>
						<p className="text-lg text-white/90">
							Acesse sua conta para contratar serviços, receber oportunidades e acompanhar suas atividades.
						</p>
					</div>
				</div>

				{/* Lado direito: Formulário de login */}
				<div className="flex w-full lg:w-1/2 min-h-screen items-center justify-center px-4 pt-[calc(2rem+var(--safe-top))] pb-[calc(2rem+var(--safe-bottom))] sm:px-6 lg:px-8 bg-white">
					<div className="w-full max-w-sm space-y-8">
						{/* Header */}
						<div>
							{/* O logo é link só na web, pelo mesmo motivo do "Voltar à tela inicial"
							    lá embaixo: no app ele apontaria para esta própria tela. Sem o link
							    ele continua sendo a marca no topo do formulário -- que é todo o
							    papel que lhe resta ali. */}
							<div className="flex justify-center -mt-4 mb-8">
								{ehNativo() ? (
									<img
										className="h-[4.8rem] w-auto"
										src="/images/logo.svg"
										alt="Nova Faxina"
									/>
								) : (
									<Link to="/">
										<img
											className="h-[4.8rem] w-auto cursor-pointer"
											src="/images/logo.svg"
											alt="Nova Faxina"
										/>
									</Link>
								)}
							</div>
							<h1 className="t-pagina mb-2">
								Entrar
							</h1>
							<p className="text-muted">
								Use seu CPF ou e-mail para continuar
							</p>
						</div>

						{/* Formulário */}
						<form onSubmit={handleLogin} className="space-y-5">
							{/* Mensagem de erro */}
							{error && (
								<div
									role="alert"
									aria-live="polite"
									className="bg-error/10 border-caixa border-error/30 rounded-xl p-4 flex items-start gap-3 animate-slide-down"
								>
									<div className="text-error mt-0.5" aria-hidden="true">⚠️</div>
									<div>
										<p className="text-sm font-medium text-error">{error}</p>
										{credenciaisInvalidas && (
											<p className="text-sm text-error/80 mt-1">
												Ainda não tem cadastro?{' '}
												<Link to="/usuario/novo" state={dadosParaOCadastro(username)} className="font-semibold underline hover:text-error">
													Crie sua conta
												</Link>
											</p>
										)}
									</div>
								</div>
							)}

							{/* Campo de CPF ou e-mail. CPF na frente, no rótulo e no placeholder: é o que o
							    público tem mais à mão -- e o cadastro também começa por ele. */}
							<div className="group">
								<label
									htmlFor="login"
									className="block text-sm font-semibold text-dark mb-2 transition-colors group-focus-within:text-primary"
								>
									CPF ou e-mail
								</label>
								<div className="relative">
									{/* O cartão de identidade é o padrão, alinhado com o placeholder (a máscara de
									    CPF); a cartinha entra quando o que está sendo digitado é e-mail. Os dois
									    ficam sobrepostos e trocam por opacidade, em vez de um substituir o outro
									    de um quadro para o outro. */}
									<div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-muted group-focus-within:text-primary transition-colors">
										<span className="relative block h-5 w-5">
											<Icon
												name="id_card"
												className={`absolute inset-0 h-5 w-5 transition-opacity duration-200 ${digitandoEmail ? 'opacity-0' : 'opacity-100'}`}
											/>
											<Icon
												name="mail"
												className={`absolute inset-0 h-5 w-5 transition-opacity duration-200 ${digitandoEmail ? 'opacity-100' : 'opacity-0'}`}
											/>
										</span>
									</div>
									<input
										id="login"
										type="text"
										placeholder="000.000.000-00"
										value={username}
										onChange={handleUsernameChange}
										onBlur={revelarSenha}
										onKeyDown={handleUsernameKeyDown}
										className="input pl-12 py-3"
										maxLength={200}
										disabled={isLoading}
										required
										autoFocus
										autoComplete="username"
										enterKeyHint="next"
										aria-label="CPF ou e-mail"
									/>
								</div>
							</div>

							{/* Campo de Senha -- revelado com fade depois do e-mail/CPF (ver `mostrarSenha`).
							    A altura anima por grid-template-rows (.campo-revelavel em styles/index.css),
							    então o campo vive num filho único. O `!mt-0` tira o espaçamento do space-y-5
							    do invólucro, que fechado tem altura zero e não pode ocupar espaço; o `pt-5`
							    do filho o repõe por dentro, crescendo junto com o campo. O padding de 0.25rem
							    com margem negativa é para o anel de foco do input não ser cortado pelo
							    overflow hidden do filho. */}
							<div className="campo-revelavel !mt-0" data-aberto={mostrarSenha}>
								<div ref={senhaConteudoRef} className="group pt-5 px-1 -mx-1 pb-1 -mb-1">
									<label
										htmlFor="senha"
										className="block text-sm font-semibold text-dark mb-2 transition-colors group-focus-within:text-primary"
									>
										Senha
									</label>
									<div className="relative">
										<div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-muted group-focus-within:text-primary transition-colors">
											<Icon name="lock" />
										</div>
										<input
											id="senha"
											ref={passwordRef}
											type={showPassword ? 'text' : 'password'}
											placeholder="Digite sua senha"
											value={password}
											onChange={(e) => setPassword(e.target.value)}
											onKeyDown={handlePasswordKeyEvent}
											onKeyUp={handlePasswordKeyEvent}
											className="input pl-12 pr-12 py-3"
											maxLength={50}
											disabled={isLoading}
											required
											autoComplete="current-password"
											aria-label="Senha"
										/>
										<button
											type="button"
											onClick={() => setShowPassword(!showPassword)}
											className="absolute inset-y-0 right-0 pr-4 flex items-center text-muted hover:text-dark transition-colors"
											aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
											disabled={isLoading}
										>
											{showPassword ? (
												<Icon name="visibility_off" />
											) : (
												<Icon name="visibility" />
											)}
										</button>
									</div>
									{capsLockOn && (
										<p role="alert" className="mt-2 text-xs font-medium text-amber-600">
											Caps Lock está ativado
										</p>
									)}
								</div>
							</div>

							{/* Botão de Entrar */}
							<button
								type="submit"
								disabled={!username || !password || isLoading}
								className="btn-primary w-full justify-center py-3 mt-6"
							>
								{isLoading ? (
									<>
										<Icon name="progress_activity" className="h-5 w-5 animate-spin" />
										<span>Entrando...</span>
									</>
								) : (
									'Entrar'
								)}
							</button>
						</form>

						{/* Divisor. No celular o "ou" e o "Criar conta" ficam mais perto do "Entrar"
						    (16px em vez dos 32px do space-y-8): o par de botões cabe junto na tela.
						    O `!` vence o seletor do space-y, que é mais específico que uma classe só. */}
						<div className="relative !mt-4 sm:!mt-8">
							<div className="absolute inset-0 flex items-center">
								<div className="w-full border-t border-border"></div>
							</div>
							<div className="relative flex justify-center text-sm">
								<span className="px-3 bg-background text-muted">ou</span>
							</div>
						</div>

						{/* Links adicionais */}
						<div className="space-y-3 !mt-4 sm:!mt-8">
							<Link
								to="/usuario/novo"
								state={dadosParaOCadastro(username)}
								className="btn w-full justify-center py-3 border border-border text-primary hover:bg-background focus:ring-primary"
								tabIndex={isLoading ? -1 : 0}
							>
								Criar conta
							</Link>

							<Link
								to="/esqueciminhasenha"
								className="w-full block text-center text-primary hover:text-primaryHover font-medium text-sm transition-colors py-4"
								tabIndex={isLoading ? -1 : 0}
							>
								Esqueci minha senha
							</Link>

							{/* Só na web. No app esta é a tela de abertura: "/" resolve para o próprio
							    login enquanto não há sessão (ver RaizDoApp em App.jsx), então o link
							    voltaria para onde a pessoa já está — um caminho que não leva a lugar
							    nenhum, oferecido como se levasse. Na web ele continua fazendo sentido:
							    lá "/" é a vitrine, e quem abriu o login sem querer precisa da saída. */}
							{!ehNativo() && (
								<Link
									to="/"
									className="flex items-center justify-center gap-2 text-muted hover:text-dark font-medium text-sm transition-colors py-2"
									tabIndex={isLoading ? -1 : 0}
								>
									<Icon name="arrow_back" />
									Voltar à tela inicial
								</Link>
							)}

							{/* Convite para o app: só no navegador de um Android (o link da loja não
							    serve em outro sistema, e no app ele já está instalado) e só no celular
							    (md:hidden, o mesmo corte do selo mobile do Footer). A linha legal vai
							    junto porque, na web, esta tela não tem o Footer onde ela mora. */}
							{appAndroidPublicado() && ehAndroidNoNavegador() && (
								<div className="flex flex-col items-center gap-2 pt-2 text-center md:hidden">
									<SeloGooglePlay origem="login" className="h-12" />
									<p className="text-xs text-dark/35">
										Google Play e o logotipo do Google Play são marcas da Google LLC.
									</p>
								</div>
							)}
						</div>

						{/* Rodapé */}
						<div className="text-center text-xs text-muted pt-4 border-t border-border">
							<p>
								Ao entrar, você concorda com nossos<br></br>{' '}
								<Link to="/termos-de-uso" className="text-primary hover:underline">
									Termos de Uso
								</Link>{' '}
								e{' '}
								<Link to="/politica-de-privacidade" className="text-primary hover:underline">
									Política de Privacidade
								</Link>
							</p>
						</div>
						{/* A nota do reCAPTCHA some no app, onde ele não roda: afirmar uma proteção que
						    não está lá é pior do que não afirmar nada.

						    A versão fica aqui porque, na web, esta tela não tem o Footer, que é onde as
						    outras a mostram. text-dark/20 é o mesmo tom da assinatura mobile do Footer --
						    presente para quem procura, invisível para quem não. Os dois no mesmo bloco
						    para a versão não ganhar os 2rem do space-y-8 só para ela.

						    No app o bloco inteiro some: o reCAPTCHA não roda, e a versão já está na linha
						    legal do rodapé, que lá esta tela tem (ver App.jsx). */}
						{!ehNativo() && (
							<div className="text-center text-xs space-y-1">
								<p className="text-muted/70">Esta página é protegida pelo Google reCAPTCHA.</p>
								<p className="text-dark/20">{APP_VERSION}</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
};

export default Login;
